import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Divider,
  FormControlLabel,
  Checkbox,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  ArrowBack,
  ChatBubbleOutlineOutlined,
  DeleteOutline,
  PersonOutlined,
} from '@mui/icons-material';
import { createCliente, updateCliente, getRestaurantes } from '../../services/api';
import { fetchMessages, clearConversation } from '../../../Atendabot/services/api.ts';
import MessageBubble from '../../../Atendabot/components/conversations/MessageBubble.tsx';

const emptyCli = {
  nome: '',
  telefone: '',
  restaurante_id: '',
  thread: '',
  active: true,
  transbordo: false,
};

const PAGE_BG = `
  radial-gradient(ellipse at 0% 0%, rgba(99, 102, 241, 0.07), transparent 45%),
  radial-gradient(ellipse at 100% 0%, rgba(124, 58, 237, 0.05), transparent 45%),
  linear-gradient(180deg, #f7f9ff 0%, #f8fafc 60%, #f7f9ff 100%)
`;

const CARD_SX = {
  borderRadius: '20px',
  border: '1px solid rgba(15, 23, 42, 0.06)',
  boxShadow: '0 14px 36px -14px rgba(15, 23, 42, 0.10), 0 0 0 1px rgba(15, 23, 42, 0.02)',
  background: '#ffffff',
  overflow: 'hidden',
};

const makeIconBox = (color, bg, border) => ({
  width: 36,
  height: 36,
  borderRadius: '10px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: bg,
  color,
  flexShrink: 0,
  border: `1px solid ${border}`,
});

const ICON_INDIGO = makeIconBox(
  '#6366f1',
  'linear-gradient(135deg, rgba(99,102,241,0.14) 0%, rgba(124,58,237,0.10) 100%)',
  'rgba(99,102,241,0.18)',
);

const fieldSx = { '& .MuiOutlinedInput-root': { borderRadius: '12px' } };

const ClienteFormPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const editing = id && id !== 'new';
  const [form, setForm] = useState(location.state || emptyCli);
  const [restaurantes, setRestaurantes] = useState([]);
  const [messages, setMessages] = useState([]);

  useEffect(() => {
    const loadRestaurantes = async () => {
      const data = await getRestaurantes();
      setRestaurantes(data);
    };
    loadRestaurantes();
  }, []);

  useEffect(() => {
    if (editing) {
      fetchMessages(id).then(setMessages).catch(() => {});
    }
  }, [editing, id]);

  const handleChange = (name) => (e) => setForm({ ...form, [name]: e.target.value });
  const handleCheck  = (name) => (e) => setForm({ ...form, [name]: e.target.checked });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (editing) await updateCliente(id, form);
    else await createCliente(form);
    navigate(-1);
  };

  const handleClear = async () => {
    await clearConversation(id);
    setMessages([]);
  };

  return (
    <Box sx={{ minHeight: '100vh', background: PAGE_BG, py: { xs: 3, md: 4 } }}>
      <Box sx={{ maxWidth: 600, mx: 'auto', px: { xs: 2, md: 3 } }}>
        <Box component="form" onSubmit={handleSubmit} autoComplete="off">

          {/* ── Buttons row (outside card) ── */}
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2.5}>
            <Button
              variant="text"
              startIcon={<ArrowBack />}
              onClick={() => navigate(-1)}
              sx={{
                color: '#475569', fontWeight: 600, borderRadius: '12px', px: 1.5, py: 0.75,
                '&:hover': { background: 'rgba(99,102,241,0.08)', color: '#6366f1' },
              }}
            >
              Voltar
            </Button>
            <Button
              type="submit"
              variant="contained"
              sx={{
                borderRadius: '12px', px: 3, py: 1, fontWeight: 700, boxShadow: 'none',
                bgcolor: '#6366f1', '&:hover': { bgcolor: '#4f46e5', boxShadow: '0 4px 14px rgba(99,102,241,0.3)' },
                transition: 'all 150ms ease',
              }}
            >
              Salvar
            </Button>
          </Stack>

          {/* ── Form card ── */}
          <Box sx={{ ...CARD_SX, mb: 2.5 }}>
            <Box sx={{ height: 4, background: 'linear-gradient(90deg, #6366f1 0%, #8b5cf6 60%, #a78bfa 100%)' }} />
            <Box sx={{ p: { xs: 2.5, md: 3 } }}>

              {/* Header */}
              <Stack direction="row" alignItems="center" gap={1.5} mb={2.5}>
                <Box sx={{ ...makeIconBox('#6366f1', 'linear-gradient(135deg, rgba(99,102,241,0.14) 0%, rgba(124,58,237,0.10) 100%)', 'rgba(99,102,241,0.18)'), width: 48, height: 48, borderRadius: '14px' }}>
                  <PersonOutlined sx={{ fontSize: 26 }} />
                </Box>
                <Box>
                  <Typography fontWeight={800} sx={{ color: '#0f172a', fontSize: '1.2rem', letterSpacing: '-0.02em', lineHeight: 1.2, mb: 0.25 }}>
                    {editing ? 'Editar Cliente' : 'Novo Cliente'}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#64748b' }}>
                    {editing
                      ? 'Atualize os dados do cliente.'
                      : 'Preencha os dados para cadastrar um novo cliente.'}
                  </Typography>
                </Box>
              </Stack>

              <Divider sx={{ mb: 2.5, borderColor: 'rgba(15,23,42,0.06)' }} />

              {/* Fields */}
              <Stack spacing={2}>
                <Stack direction="row" alignItems="center" gap={1} mb={0.5}>
                  <Box sx={ICON_INDIGO}><PersonOutlined sx={{ fontSize: 18 }} /></Box>
                  <Typography fontWeight={700} sx={{ color: '#0f172a', fontSize: '0.9rem' }}>Dados do cliente</Typography>
                </Stack>

                <TextField label="Nome" fullWidth required value={form.nome} onChange={handleChange('nome')} sx={fieldSx} />
                <TextField label="Telefone" fullWidth required value={form.telefone} onChange={handleChange('telefone')} sx={fieldSx} />
                <TextField
                  select label="Restaurante" fullWidth value={form.restaurante_id || ''} onChange={handleChange('restaurante_id')} sx={fieldSx}
                >
                  <MenuItem value="">Nenhum</MenuItem>
                  {restaurantes.map((r) => <MenuItem key={r.id} value={r.id}>{r.nome}</MenuItem>)}
                </TextField>

                <Stack direction="row" spacing={2}>
                  <Box sx={{ flex: 1, px: 1.5, py: 1, borderRadius: '12px', background: 'linear-gradient(180deg, #fff 0%, #fbfcff 100%)', border: '1px solid rgba(15,23,42,0.08)', transition: 'border-color 0.2s', '&:hover': { borderColor: 'rgba(99,102,241,0.32)' } }}>
                    <FormControlLabel
                      control={<Checkbox checked={!!form.active} onChange={handleCheck('active')} sx={{ color: '#6366f1', '&.Mui-checked': { color: '#6366f1' } }} />}
                      label={<Typography sx={{ fontWeight: 600, fontSize: '0.875rem', color: '#0f172a' }}>Ativo</Typography>}
                      sx={{ m: 0 }}
                    />
                  </Box>
                  <Box sx={{ flex: 1, px: 1.5, py: 1, borderRadius: '12px', background: 'linear-gradient(180deg, #fff 0%, #fbfcff 100%)', border: '1px solid rgba(15,23,42,0.08)', transition: 'border-color 0.2s', '&:hover': { borderColor: 'rgba(99,102,241,0.32)' } }}>
                    <FormControlLabel
                      control={<Checkbox checked={!!form.transbordo} onChange={handleCheck('transbordo')} sx={{ color: '#6366f1', '&.Mui-checked': { color: '#6366f1' } }} />}
                      label={<Typography sx={{ fontWeight: 600, fontSize: '0.875rem', color: '#0f172a' }}>Transbordo</Typography>}
                      sx={{ m: 0 }}
                    />
                  </Box>
                </Stack>
              </Stack>
            </Box>
          </Box>
        </Box>

        {/* ── Mensagens card (editing only) ── */}
        {editing && (
          <Box sx={CARD_SX}>
            <Box sx={{ height: 4, background: 'linear-gradient(90deg, #0891b2 0%, #6366f1 100%)' }} />
            <Box sx={{ p: { xs: 2.5, md: 3 } }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
                <Stack direction="row" alignItems="center" gap={1.5}>
                  <Box sx={{ ...makeIconBox('#0891b2', 'linear-gradient(135deg, rgba(8,145,178,0.14) 0%, rgba(6,182,212,0.10) 100%)', 'rgba(8,145,178,0.18)'), width: 40, height: 40, borderRadius: '12px' }}>
                    <ChatBubbleOutlineOutlined sx={{ fontSize: 20 }} />
                  </Box>
                  <Box>
                    <Typography fontWeight={700} sx={{ color: '#0f172a', fontSize: '0.975rem', letterSpacing: '-0.01em' }}>
                      Últimas Mensagens
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#64748b', fontSize: '0.82rem' }}>
                      Histórico da conversa com o cliente.
                    </Typography>
                  </Box>
                </Stack>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<DeleteOutline sx={{ fontSize: 16 }} />}
                  onClick={handleClear}
                  sx={{
                    borderRadius: '10px', fontWeight: 600, fontSize: '0.8rem',
                    borderColor: '#fecdd3', color: '#be123c',
                    '&:hover': { bgcolor: '#fff1f2', borderColor: '#fca5a5' },
                  }}
                >
                  Limpar conversa
                </Button>
              </Stack>

              <Divider sx={{ mb: 2, borderColor: 'rgba(15,23,42,0.06)' }} />

              <Box
                sx={{
                  maxHeight: 320,
                  overflowY: 'auto',
                  p: 1.5,
                  background: '#f8fafc',
                  borderRadius: '14px',
                  border: '1px solid rgba(15,23,42,0.06)',
                  scrollbarWidth: 'thin',
                  scrollbarColor: '#e2e8f0 transparent',
                }}
              >
                {messages.length === 0 ? (
                  <Typography sx={{ color: '#94a3b8', textAlign: 'center', py: 4, fontWeight: 500, fontSize: '0.9rem' }}>
                    Nenhuma mensagem encontrada.
                  </Typography>
                ) : (
                  messages.map((m) => <MessageBubble key={m.id} message={m} />)
                )}
              </Box>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default ClienteFormPage;
